import { buildAposPlan } from "@/components/aposEngine/simpleEngine";
import {
  APOS_STATE_KEYS,
  STATE_KNOWLEDGE,
} from "@/components/aposEngine/knowledgeBank";
import { ARCHETYPES } from "@/components/assessment/assessmentData";
import { computeArchetype, loadProfileLocal } from "@/components/assessment/assessmentScoring";
import { loadRoutineSettings } from "@/components/daily/routineSettings";
import { nutritionStateMatchContext } from "@/components/daily/bodyMassEngine";
import {
  computeFunctionalStateMatch,
  fallbackForStateMatchBand,
} from "@/components/daily/functionalStateMatch";

export const DAILY_LOG_KEY = "forr:daily-state-log:v2";
export const DAILY_SETTINGS_KEY = "forr:daily-state-settings:v2";

export const STATE_LEVELS = [
  { value: 1, label: "High strain", short: "High strain" },
  { value: 2, label: "Elevated demand", short: "Elevated demand" },
  { value: 3, label: "Mixed", short: "Mixed response" },
  { value: 4, label: "Steady", short: "Steady response" },
  { value: 5, label: "Capacity available", short: "Capacity available" },
];

export const DIRECT_STATE_OPTIONS = APOS_STATE_KEYS.map((key) => ({
  key,
  label: STATE_KNOWLEDGE[key].label,
  system: STATE_KNOWLEDGE[key].system,
}));

export const ENVIRONMENTS = [
  { key: "inside", label: "Inside", resources: ["indoor-space"] },
  { key: "outside", label: "Outside", resources: ["outside", "safe-walk"] },
  { key: "gym", label: "Gym", resources: ["gym", "equipment"] },
  { key: "limited", label: "Limited space", resources: ["none"] },
];

export const TIME_OPTIONS = [
  { value: 3, label: "2–5 min", availability: "2-5" },
  { value: 7, label: "5–10 min", availability: "5-10" },
  { value: 15, label: "10–20 min", availability: "10-20" },
  { value: 25, label: "20–30 min", availability: "20-30" },
  { value: 30, label: "30+ min", availability: "30-plus" },
];

const legacyStateKeys = ({ level, energy }) => {
  if (level <= 1) return [energy <= 2 ? "depleted" : "anxious-overwhelmed"];
  if (level === 2) return [energy <= 2 ? "sad-low" : "restless-activated"];
  if (level === 3) return [energy <= 2 ? "depleted" : "restless-activated"];
  if (level === 4) return ["calm-steady"];
  return ["positive-energized"];
};

const intensityForLevel = (level) => ({ 1: 5, 2: 4, 3: 3, 4: 2, 5: 2 }[level] || 3);

const reportedStates = (checkIn) => {
  const explicit = [checkIn.primaryState, checkIn.secondaryState].filter(Boolean);
  if (explicit.length) return [...new Set(explicit)].slice(0, 2);
  if (Array.isArray(checkIn.states) && checkIn.states.length) {
    return [...new Set(checkIn.states)].slice(0, 2);
  }
  return legacyStateKeys(checkIn);
};

export function getSavedAssessment() {
  const saved = loadProfileLocal();
  if (!saved?.a) return null;
  return {
    id: saved.id || null,
    answers: saved.a,
    identity: saved.i || null,
    profile: computeArchetype(saved.a),
    schemaVersion: Number(saved.v) || 0,
    assessmentModel: saved.assessmentModel || null,
    baselineVector:
      saved.baselineVector || saved.nervousSystemProfile?.vector || saved.a?.robustProfile?.vector || null,
    nervousSystemProfile:
      saved.nervousSystemProfile || saved.a?.robustProfile || null,
    completedAt: saved.t,
  };
}

const planInput = ({ assessment, checkIn, calendarEvents = [], entries = [], now = new Date() }) => {
  const environment =
    ENVIRONMENTS.find((item) => item.key === checkIn.environment) || ENVIRONMENTS[0];
  const time =
    TIME_OPTIONS.find((item) => item.value === Number(checkIn.minutes)) || TIME_OPTIONS[1];
  const states = reportedStates(checkIn);
  const routineSettings = loadRoutineSettings() || {};
  const nutritionContext = nutritionStateMatchContext(
    routineSettings.bodyMassNutrition || {},
    assessment.identity || {},
  );
  const stateMatch = computeFunctionalStateMatch({
    checkIn,
    calendarEvents,
    entries,
    nutritionContext,
    baselineVector:
      assessment.baselineVector ||
      assessment.nervousSystemProfile?.vector ||
      assessment.profile?.nervousSystemProfile?.vector ||
      assessment.answers?.robustProfile?.vector ||
      null,
    now,
  });
  const currentOverride = {
    ...(assessment.answers.current || {}),
    source: "app-check-in",
    states,
    primaryState: states[0],
    secondaryState: states[1] || null,
    intensity: Number(checkIn.intensity) || intensityForLevel(checkIn.level),
    energy: Number(checkIn.energy) || 3,
    actionPressure: Number(checkIn.actionPressure) || 3,
    behavioralChoice: Number(checkIn.behavioralChoice) || 3,
    somaticResponse: Number(checkIn.somaticResponse) || 3,
    cognitiveCapture: Number(checkIn.cognitiveCapture) || 3,
    recoveryDifficulty: Number(checkIn.recoveryDifficulty) || 3,
    physicalReadiness: checkIn.physicalReadiness,
    hydration: checkIn.hydration,
    nourishment: checkIn.nourishment,
    urge: checkIn.urge || "none",
    conditioningLevel:
      checkIn.conditioningLevel || assessment.answers.schedule?.conditioningLevel,
    environment: environment.key,
    minutes: Number(checkIn.minutes) || time.value,
    stateMatch,
    nutritionContext,
  };
  const bedtime =
    routineSettings.bedtime ||
    assessment.answers.schedule?.bedtime ||
    assessment.answers.schedule?.times?.bedtime ||
    "22:00";
  const bedtimeAvailability =
    routineSettings.bedtimeAvailability ||
    assessment.answers.schedule?.bedtimeAvailability ||
    assessment.answers.schedule?.availability?.bedtime ||
    "5-10";
  const answers = {
    ...assessment.answers,
    current: {
      ...(assessment.answers.current || {}),
      ...currentOverride,
    },
    schedule: {
      ...(assessment.answers.schedule || {}),
      resources: environment.resources,
      bedtime,
      bedtimeAvailability,
      availability: {
        ...(assessment.answers.schedule?.availability || {}),
        bedtime: bedtimeAvailability,
      },
      times: {
        ...(assessment.answers.schedule?.times || {}),
        bedtime,
      },
    },
  };
  return { answers, currentOverride, stateMatch };
};

export function buildDailyRecommendation({
  assessment,
  checkIn,
  fallbackLevel,
  calendarEvents = [],
  entries = [],
  now = new Date(),
}) {
  if (!assessment?.answers || !assessment?.profile) return null;
  const input = planInput({ assessment, checkIn, calendarEvents, entries, now });
  const requestedFallback = fallbackLevel || fallbackForStateMatchBand(input.stateMatch.finalBand);
  const plan = buildAposPlan({
    profile: assessment.profile,
    answers: input.answers,
    currentOverride: input.currentOverride,
    fallbackLevel: requestedFallback,
    now,
  });
  return {
    ...plan,
    stateMatch: input.stateMatch,
  };
}

export function rematchDailyRecommendation({
  assessment,
  checkIn,
  fallbackLevel,
  calendarEvents = [],
  entries = [],
  now = new Date(),
}) {
  return buildDailyRecommendation({
    assessment,
    checkIn,
    fallbackLevel,
    calendarEvents,
    entries,
    now,
  });
}

export function loadDailyLog() {
  try {
    const currentRaw = localStorage.getItem(DAILY_LOG_KEY);
    if (currentRaw) {
      const current = JSON.parse(currentRaw);
      if (Array.isArray(current)) return current;
    }
    const legacy = JSON.parse(localStorage.getItem("forr:daily-state-log:v1") || "[]");
    return Array.isArray(legacy) ? legacy : [];
  } catch (_error) {
    return [];
  }
}

export function saveDailyLog(entries) {
  const saved = (entries || []).slice(0, 180);
  try {
    localStorage.setItem(DAILY_LOG_KEY, JSON.stringify(saved));
  } catch (_error) {
    // Storage may be disabled; the current session still works.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("apos-local-data-changed", {
        detail: { source: "daily-log", recordCount: saved.length },
      }),
    );
  }
  return saved;
}

export function makeCheckInRecord({ checkIn, plan, biometrics, assessmentCompletedAt }) {
  return {
    id: `checkin-${Date.now()}`,
    createdAt: new Date().toISOString(),
    baselineCompletedAt: assessmentCompletedAt || null,
    levelBefore: Number(checkIn.level),
    energyBefore: Number(checkIn.energy),
    actionPressureBefore: Number(checkIn.actionPressure) || 3,
    behavioralChoiceBefore: Number(checkIn.behavioralChoice) || 3,
    somaticResponseBefore: Number(checkIn.somaticResponse) || 3,
    cognitiveCaptureBefore: Number(checkIn.cognitiveCapture) || 3,
    recoveryDifficultyBefore: Number(checkIn.recoveryDifficulty) || 3,
    reportedStates: plan.currentState.labels,
    system: plan.currentState.system,
    physiologicalTarget: plan.currentState.target,
    environment: checkIn.environment,
    minutesAvailable: Number(checkIn.minutes),
    physicalReadiness: checkIn.physicalReadiness,
    hydration: checkIn.hydration,
    nourishment: checkIn.nourishment,
    profileKey: plan.profileKey,
    stateMatch: plan.stateMatch || null,
    recommendation: {
      title: plan.immediate.title,
      activityKey: plan.immediate.activityKey || null,
      activityType: plan.immediate.activityType || null,
      duration: plan.immediate.duration,
      instruction: plan.immediate.instruction,
      fallbackLevel: plan.immediate.fallbackLevel,
    },
    preparation: plan.preparation,
    cooldown: plan.cooldown,
    routines: plan.routines,
    alternatives: (plan.week.recommendations || []).slice(0, 5),
    biometrics: biometrics || null,
    completed: false,
    completedAt: null,
    response: null,
    responseMagnitude: null,
    changeMagnitude: null,
    improvementRating: null,
    adaptivePoints: 0,
    feltWorse: false,
  };
}

export function completeRecord(
  entries,
  id,
  {
    responseMagnitude,
    changeMagnitude,
    improvementRating,
    feltWorse = false,
    levelAfter = null,
    response = null,
  },
) {
  const rawMagnitude = responseMagnitude ?? changeMagnitude ?? improvementRating;
  const normalizedMagnitude = feltWorse
    ? Math.max(1, Math.min(5, Number(rawMagnitude) || 3))
    : Math.max(1, Math.min(5, Number(rawMagnitude) || 1));
  return entries.map((entry) =>
    entry.id === id
      ? {
          ...entry,
          completed: true,
          completedAt: new Date().toISOString(),
          levelAfter: levelAfter == null ? entry.levelAfter : Number(levelAfter),
          response:
            feltWorse
              ? "worse"
              : response || (normalizedMagnitude === 1 ? "same" : "changed"),
          responseMagnitude: normalizedMagnitude,
          changeMagnitude: normalizedMagnitude,
          adaptivePoints: 0,
          improvementRating: null,
          feltWorse: Boolean(feltWorse),
        }
      : entry,
  );
}

const dayKey = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

export function getParticipationSummary(entries, now = new Date()) {
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
  sevenDaysAgo.setHours(0, 0, 0, 0);
  const week = entries.filter((entry) => new Date(entry.createdAt) >= sevenDaysAgo);
  const completed = week.filter((entry) => entry.completed);
  const uniqueDays = new Set(week.map((entry) => dayKey(entry.createdAt)).filter(Boolean)).size;
  const responseChecks = entries.filter(
    (entry) =>
      entry.responseMagnitude ||
      entry.changeMagnitude ||
      entry.improvementRating ||
      entry.feltWorse ||
      ["same", "changed", "better", "worse"].includes(entry.response),
  ).length;
  const points = entries.reduce(
    (total, entry) =>
      total +
      1 +
      (entry.completed ? 2 : 0) +
      (entry.responseMagnitude || entry.changeMagnitude || entry.improvementRating || entry.feltWorse ? 1 : 0),
    0,
  );
  const badges = [
    { key: "first", label: "First check-in", emoji: "🌱", earned: entries.length >= 1 },
    { key: "return", label: "Returned 3 days", emoji: "🧭", earned: new Set(entries.map((entry) => dayKey(entry.createdAt)).filter(Boolean)).size >= 3 },
    { key: "practice", label: "5 completed inputs", emoji: "⚡", earned: entries.filter((entry) => entry.completed).length >= 5 },
    { key: "observe", label: "5 response observations", emoji: "🔎", earned: responseChecks >= 5 },
    { key: "learn", label: "20 response observations", emoji: "🧠", earned: responseChecks >= 20 },
  ];
  return {
    week,
    completed,
    uniqueDays,
    points,
    responseChecks,
    badges,
  };
}

export function getRelevantEducation({ plan, biometrics }) {
  if (biometrics?.hrvMs) {
    return {
      title: "Use HRV as supporting context",
      body: "Compare camera HRV with your own recent readings under similar conditions. The directly reported state remains the main selector; HRV adds context but does not independently decide the emotional state or intervention.",
    };
  }
  if (plan?.stateMatch) {
    return {
      title: "Nervous-system state matching + nervous-system training = regulation",
      body: `${plan.stateMatch.explanation} Emotion is meaningful context; mobilization, action pressure, usable energy, behavioral choice, body response, attention, recovery, existing training demand, and physical capacity refine the relative dose.`,
    };
  }
  if (plan?.preparation?.required) {
    return {
      title: "Preparation supports the selected output",
      body: "Hydration, nourishment, setup, and environmental changes can support the selected output. Preparation should not duplicate the main intervention.",
    };
  }
  if (plan?.stateInterpretation) {
    return {
      title: plan.stateInterpretation.system,
      body: `${plan.stateInterpretation.participantExplanation} ${plan.stateInterpretation.targetExplanation}`,
    };
  }
  return {
    title: "Report the nervous-system state, then match the output",
    body: "APOS uses the directly reported nervous-system response to select the physiological direction, then uses readiness, conditioning, behavioral choice, current load, time, environment, nutrition/recovery context, and prior responses to choose the relative dose.",
  };
}
